/**
 * useInspectionLotInfo Hook
 * Fetches batch, product, and country data for a selected inspection lot
 */

import { useState, useEffect, useRef } from 'react';
import { BRRApiClient } from '../services/api';
import { InspectionLotInfoResponse, BRRLevel } from '../services/types';

interface UseInspectionLotInfoOptions {
  apiClient: BRRApiClient;
  inspectionLotId: string | null;
  brrLevel: BRRLevel | null;
}

interface UseInspectionLotInfoReturn {
  data: InspectionLotInfoResponse | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Hook that calls the inspection lot info API when a lot is selected.
 * Uses a requestIdRef counter to prevent stale responses from overwriting newer ones.
 *
 * @example
 * const { data, loading, error } = useInspectionLotInfo({
 *   apiClient: brrApi,
 *   inspectionLotId: selectedLotId,
 *   brrLevel: 'na'
 * });
 */
export const useInspectionLotInfo = ({
  apiClient,
  inspectionLotId,
  brrLevel
}: UseInspectionLotInfoOptions): UseInspectionLotInfoReturn => {
  const [data, setData] = useState<InspectionLotInfoResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!inspectionLotId || !brrLevel) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    const currentRequestId = ++requestIdRef.current;

    const fetchInfo = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiClient.getInspectionLotInfo(inspectionLotId, brrLevel);

        // Only update state if this is still the latest request
        if (currentRequestId === requestIdRef.current) {
          setData(response);
        }
      } catch (err) {
        if (currentRequestId === requestIdRef.current) {
          const error = err instanceof Error ? err : new Error(String(err));
          setError(error);
          setData(null);
        }
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };

    void fetchInfo();
  }, [apiClient, inspectionLotId, brrLevel]);

  return { data, loading, error };
};
